import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../utils/api';
import { format } from 'date-fns';

// ── Admin Dashboard ───────────────────────────────────────
export function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{ api.get('/admin/dashboard').then(r=>setData(r.data)).finally(()=>setLoading(false)); },[]);

  if (loading) return <DashboardLayout title="Admin Dashboard"><div className="loading-screen"><div className="spinner"/></div></DashboardLayout>;

  const s = data?.stats || {};

  return (
    <DashboardLayout title="Admin Dashboard" subtitle="Platform overview">
      <div className="stats-grid" style={{marginBottom:28}}>
        {[
          {label:'Total tutors',     value:s.total_tutors||0},
          {label:'Pending approval', value:s.pending_tutors||0, alert:s.pending_tutors>0},
          {label:'Active tutors',    value:s.active_tutors||0,  green:true},
          {label:'Total parents',    value:s.total_parents||0},
          {label:'Active bookings',  value:s.active_bookings||0},
          {label:'Lessons done',     value:s.completed_lessons||0},
          {label:'Open disputes',    value:s.open_disputes||0,   alert:s.open_disputes>0},
          {label:'Open concerns',    value:s.open_concerns||0,   alert:s.open_concerns>0},
        ].map(item=>(
          <div key={item.label} className="stat-card">
            <div className="stat-card-label">{item.label}</div>
            <div className="stat-card-value" style={{color:item.alert?'var(--error)':item.green?'var(--success)':'var(--ink)'}}>{item.value}</div>
          </div>
        ))}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
        <div className="card">
          <h3 style={{marginBottom:16,fontSize:'1rem'}}>Revenue (last 6 months)</h3>
          {!data?.monthlyRevenue?.length
            ? <p style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>No revenue data yet.</p>
            : <table style={{width:'100%',fontSize:'0.82rem'}}>
                <thead><tr><th style={{textAlign:'left',padding:'4px 0',color:'var(--ink-400)'}}>Month</th><th style={{textAlign:'left',padding:'4px 0',color:'var(--ink-400)'}}>Lessons</th><th style={{textAlign:'left',padding:'4px 0',color:'var(--ink-400)'}}>Revenue</th></tr></thead>
                <tbody>{data.monthlyRevenue.map((r,i)=>(
                  <tr key={i}><td style={{padding:'6px 0'}}>{r.month}</td><td>{r.lessons}</td><td style={{fontWeight:700,color:'var(--green-700)'}}>GBP{parseFloat(r.revenue||0).toFixed(2)}</td></tr>
                ))}</tbody>
              </table>
          }
        </div>
        <div className="card">
          <h3 style={{marginBottom:16,fontSize:'1rem'}}>Quick actions</h3>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <Link to="/admin/tutors/pending" className="btn btn-primary btn-sm" style={{justifyContent:'flex-start'}}>Review tutor queue ({s.pending_tutors||0})</Link>
            <Link to="/admin/tutors/approved" className="btn btn-secondary btn-sm" style={{justifyContent:'flex-start'}}>View approved tutors</Link>
            <Link to="/admin/disputes" className="btn btn-danger btn-sm" style={{justifyContent:'flex-start'}}>Open disputes ({s.open_disputes||0})</Link>
            <Link to="/admin/payouts" className="btn btn-ghost btn-sm" style={{justifyContent:'flex-start'}}>Process payouts</Link>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:768px){div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}}`}</style>
    </DashboardLayout>
  );
}

// ── Tutor Approval Queue ──────────────────────────────────
export function AdminTutorQueue() {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{ api.get('/admin/tutors/pending').then(r=>setTutors(r.data.tutors||[])).finally(()=>setLoading(false)); },[]);

  const approve = async (id, name) => {
    if (!window.confirm('Approve ' + name + '?')) return;
    await api.post('/admin/tutors/' + id + '/approve');
    setTutors(tutors.filter(t=>t.id!==id));
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    await api.post('/admin/tutors/' + id + '/reject', { reason });
    setTutors(tutors.filter(t=>t.id!==id));
  };

  return (
    <DashboardLayout title="Tutor Queue" subtitle="Applications awaiting review">
      {loading ? <div className="loading-screen"><div className="spinner"/></div>
        : tutors.length===0
        ? <div style={{textAlign:'center',padding:40,color:'var(--ink-400)'}}><div style={{fontSize:'3rem'}}>✅</div><p>Queue is clear.</p></div>
        : <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {tutors.map(t=>(
            <div key={t.id} className="card" style={{padding:20}}>
              <div style={{display:'flex',gap:16,alignItems:'flex-start',flexWrap:'wrap'}}>
                <div className="avatar-placeholder avatar-md" style={{fontSize:'0.9rem'}}>{t.full_name?.split(' ').map(w=>w[0]).join('')}</div>
                <div style={{flex:1,minWidth:200}}>
                  <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:4}}>
                    <h3 style={{margin:0,fontSize:'1rem'}}>{t.full_name}</h3>
                    <span className="badge badge-yellow">Pending</span>
                  </div>
                  <div style={{fontSize:'0.82rem',color:'var(--ink-400)',marginBottom:8}}>{t.email} · {t.country}</div>
                  {t.subjects && <div style={{fontSize:'0.8rem',color:'var(--ink-500)',marginBottom:8}}><strong>Subjects:</strong> {t.subjects}</div>}
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {[
                      {label:'Fee paid',       done:t.step_fee_paid},
                      {label:'English video',  done:t.step_english_test},
                      {label:'Bio',            done:t.step_bio_video},
                      {label:'Specialisms',    done:t.step_specialisms},
                      {label:'ID',             done:t.step_id_upload},
                      {label:'DBS',            done:t.step_dbs},
                      {label:'References',     done:t.step_references},
                      {label:'Availability',   done:t.step_availability},
                    ].map(s=>(
                      <span key={s.label} className={'badge ' + (s.done?'badge-green':'badge-red')}>{s.done?'✓':''} {s.label}</span>
                    ))}
                  </div>
                </div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <Link to={'/admin/tutors/' + t.id} className="btn btn-secondary btn-sm">Full review</Link>
                  <button className="btn btn-primary btn-sm" onClick={()=>approve(t.id,t.full_name)}>Approve</button>
                  <button className="btn btn-danger btn-sm" onClick={()=>reject(t.id)}>Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      }
    </DashboardLayout>
  );
}

// ── Tutor Detail (full review) ────────────────────────────
export function AdminTutorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/admin/tutors/' + id).then(r=>setTutor(r.data.tutor)).finally(()=>setLoading(false));
  useEffect(()=>{ load(); },[id]);

  const docAction = async (type, docId, action) => {
    if (action === 'reject') {
      const reason = window.prompt('Rejection reason:');
      if (!reason) return;
      await api.post('/admin/documents/' + type + '/' + docId + '/reject', { reason });
    } else {
      await api.post('/admin/documents/' + type + '/' + docId + '/approve');
    }
    load();
  };

  const approveTutor = async () => {
    if (!window.confirm('Approve this tutor?')) return;
    await api.post('/admin/tutors/' + id + '/approve');
    navigate('/admin/tutors/pending');
  };

  const rejectTutor = async () => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;
    await api.post('/admin/tutors/' + id + '/reject', { reason });
    navigate('/admin/tutors/pending');
  };

  const suspendTutor = async () => {
    const reason = window.prompt('Suspension reason:');
    if (!reason) return;
    await api.post('/admin/tutors/' + id + '/suspend', { reason });
    load();
  };

  const unsuspendTutor = async () => {
    if (!window.confirm('Reinstate this tutor?')) return;
    await api.post('/admin/tutors/' + id + '/unsuspend');
    load();
  };

  if (loading) return <DashboardLayout title="Tutor Detail"><div className="loading-screen"><div className="spinner"/></div></DashboardLayout>;
  if (!tutor) return <DashboardLayout title="Tutor Detail"><p>Not found.</p></DashboardLayout>;

  const statusColor = {approved:'badge-green',pending:'badge-yellow',rejected:'badge-red',suspended:'badge-red'};
  const docStatusColor = {approved:'badge-green',rejected:'badge-red',pending:'badge-yellow'};

  const Section = ({title, children}) => (
    <div className="card" style={{marginBottom:16}}>
      <h3 style={{fontSize:'1rem',marginBottom:16,paddingBottom:12,borderBottom:'1px solid var(--ink-100)'}}>{title}</h3>
      {children}
    </div>
  );

  const DocActions = ({type, docId, status}) => (
    status === 'pending' || !status ? (
      <div style={{display:'flex',gap:8,marginTop:10}}>
        <button className="btn btn-primary btn-sm" onClick={()=>docAction(type,docId,'approve')}>Approve</button>
        <button className="btn btn-danger btn-sm" onClick={()=>docAction(type,docId,'reject')}>Reject</button>
      </div>
    ) : (
      <span className={'badge ' + (docStatusColor[status]||'badge-grey')} style={{marginTop:8,display:'inline-block'}}>{status}</span>
    )
  );

  return (
    <DashboardLayout title={tutor.full_name} subtitle="Tutor application review">
      {/* Action bar */}
      <div style={{display:'flex',gap:10,marginBottom:24,flexWrap:'wrap',padding:16,background:'#fff',borderRadius:12,border:'1.5px solid var(--ink-100)'}}>
        <button className="btn btn-ghost btn-sm" onClick={()=>navigate(-1)}>Back</button>
        <span className={'badge ' + (statusColor[tutor.status]||'badge-grey')} style={{padding:'8px 16px',fontSize:'0.82rem'}}>{tutor.status}</span>
        <div style={{marginLeft:'auto',display:'flex',gap:8,flexWrap:'wrap'}}>
          {tutor.status === 'pending' && (
            <>
              <button className="btn btn-primary" onClick={approveTutor}>✓ Approve tutor</button>
              <button className="btn btn-danger" onClick={rejectTutor}>✗ Reject</button>
            </>
          )}
          {tutor.status === 'approved' && (
            <button className="btn btn-danger" onClick={suspendTutor}>Suspend tutor</button>
          )}
          {tutor.status === 'suspended' && (
            <button className="btn btn-primary" onClick={unsuspendTutor}>Reinstate tutor</button>
          )}
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>

        {/* Personal info */}
        <Section title="Personal details">
          <div style={{fontSize:'0.875rem',lineHeight:2}}>
            <div><strong>Email:</strong> {tutor.email}</div>
            <div><strong>Phone:</strong> {tutor.phone || 'Not provided'}</div>
            <div><strong>Country:</strong> {tutor.country}</div>
            {tutor.suspension_reason && <div style={{color:'var(--error)'}}><strong>Suspension reason:</strong> {tutor.suspension_reason}</div>}
          </div>
        </Section>

        {/* Bio */}
        <Section title="Bio">
          <p style={{margin:0,fontSize:'0.875rem',color:'var(--ink-500)',lineHeight:1.7}}>{tutor.bio || 'Not provided'}</p>
        </Section>

        {/* Qualifications */}
        <Section title="Qualifications">
          <p style={{margin:0,fontSize:'0.875rem',color:'var(--ink-500)',lineHeight:1.7,whiteSpace:'pre-line'}}>{tutor.qualifications || 'Not provided'}</p>
        </Section>

        {/* English test video */}
        <Section title="English language video">
          {tutor.english_video_url ? (
            <>
              <span className={'badge ' + (docStatusColor[tutor.english_test_status]||'badge-yellow')} style={{marginBottom:10,display:'inline-block'}}>{tutor.english_test_status || 'pending'}</span>
              <video controls src={tutor.english_video_url} style={{width:'100%',borderRadius:8,maxHeight:240,background:'#000',display:'block',marginBottom:8}}/>
              <DocActions type="english" docId={tutor.english_test_id} status={tutor.english_test_status}/>
            </>
          ) : <p style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>No video submitted.</p>}
        </Section>

        {/* Work experience */}
        <Section title="Work experience">
          {!tutor.work_experience?.length
            ? <p style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>None provided.</p>
            : tutor.work_experience.map((w,i)=>(
              <div key={i} style={{padding:'10px 14px',background:'var(--ink-50)',borderRadius:8,marginBottom:8}}>
                <div style={{fontWeight:700,fontSize:'0.875rem'}}>{w.role} at {w.employer}</div>
                <div style={{fontSize:'0.78rem',color:'var(--ink-400)'}}>{w.from} — {w.current ? 'Present' : w.to}</div>
                {w.description && <p style={{margin:'6px 0 0',fontSize:'0.82rem',color:'var(--ink-500)'}}>{w.description}</p>}
              </div>
            ))
          }
        </Section>

        {/* Subjects & Levels */}
        <Section title="Subjects and levels">
          <div style={{marginBottom:14}}>
            <p style={{fontWeight:700,fontSize:'0.78rem',color:'var(--ink-400)',textTransform:'uppercase',marginBottom:8}}>Subjects</p>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {tutor.subjects?.map(s=><span key={s.id} className="badge badge-green">{s.name}</span>)}
              {!tutor.subjects?.length && <span style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>None selected</span>}
            </div>
          </div>
          <div>
            <p style={{fontWeight:700,fontSize:'0.78rem',color:'var(--ink-400)',textTransform:'uppercase',marginBottom:8}}>Teaching levels</p>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {tutor.levels?.map(l=><span key={l.id} className="badge badge-blue">{l.name}</span>)}
              {!tutor.levels?.length && <span style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>None selected</span>}
            </div>
          </div>
        </Section>

        {/* ID document */}
        <Section title="ID document">
          {tutor.id_document_url ? (
            <>
              <p style={{fontSize:'0.82rem',marginBottom:8}}>Type: <strong>{tutor.document_type}</strong></p>
              {tutor.id_document_url.toLowerCase().endsWith('.pdf')
                ? <a href={tutor.id_document_url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{marginBottom:8}}>View PDF</a>
                : <img src={tutor.id_document_url} alt="ID document" style={{width:'100%',borderRadius:8,maxHeight:300,objectFit:'contain',background:'#f0f0f0',marginBottom:8}}/>
              }
              <DocActions type="id" docId={tutor.id_doc_id} status={tutor.id_status}/>
            </>
          ) : <p style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>No ID document uploaded.</p>}
        </Section>

        {/* DBS */}
        <Section title="DBS certificate">
          <p style={{fontSize:'0.875rem',marginBottom:8}}>
            Has DBS: <strong>{tutor.has_dbs ? 'Yes' : 'No'}</strong>
            {tutor.certificate_number && <> · Cert number: <strong>{tutor.certificate_number}</strong></>}
            {tutor.issue_date && <> · Issued: <strong>{tutor.issue_date}</strong></>}
          </p>
          {tutor.dbs_url && (
            <>
              {tutor.dbs_url.toLowerCase().endsWith('.pdf')
                ? <a href={tutor.dbs_url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{marginBottom:8}}>View DBS PDF</a>
                : <img src={tutor.dbs_url} alt="DBS certificate" style={{width:'100%',borderRadius:8,maxHeight:240,objectFit:'contain',background:'#f0f0f0',marginBottom:8}}/>
              }
              <DocActions type="dbs" docId={tutor.dbs_id} status={tutor.dbs_verified?'approved':null}/>
            </>
          )}
        </Section>

        {/* Educational documents */}
        {tutor.educationDocs?.length > 0 && (
          <Section title="Educational documents">
            {tutor.educationDocs.map((doc,i)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 12px',background:'var(--ink-50)',borderRadius:8,marginBottom:8}}>
                <div>
                  <div style={{fontWeight:600,fontSize:'0.875rem'}}>{doc.file_name}</div>
                  <span className={'badge ' + (doc.admin_verified?'badge-green':'badge-yellow')} style={{marginTop:4,display:'inline-block'}}>{doc.admin_verified?'Verified':'Pending'}</span>
                </div>
                <div style={{display:'flex',gap:8}}>
                  <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">View</a>
                  {!doc.admin_verified && (
                    <button className="btn btn-primary btn-sm" onClick={()=>docAction('document',doc.id,'approve')}>Verify</button>
                  )}
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* References */}
        <Section title="References">
          {!tutor.references?.length
            ? <p style={{color:'var(--ink-400)',fontSize:'0.875rem'}}>No references.</p>
            : tutor.references.map((r,i)=>(
              <div key={i} style={{padding:'10px 14px',background:'var(--ink-50)',borderRadius:8,marginBottom:8}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8}}>
                  <div>
                    <strong style={{fontSize:'0.875rem'}}>{r.ref_name}</strong>
                    <div style={{fontSize:'0.78rem',color:'var(--ink-400)'}}>{r.ref_email} {r.ref_phone ? '· ' + r.ref_phone : ''}</div>
                    {r.ref_relationship && <div style={{fontSize:'0.78rem',color:'var(--ink-400)'}}>{r.ref_relationship}</div>}
                  </div>
                  <div>
                    <span className={'badge ' + (docStatusColor[r.status]||'badge-yellow')} style={{marginBottom:6,display:'block'}}>{r.status}</span>
                    {r.status === 'pending' && (
                      <div style={{display:'flex',gap:6}}>
                        <button className="btn btn-primary btn-sm" onClick={()=>docAction('reference',r.id,'approve')}>Approve</button>
                        <button className="btn btn-danger btn-sm" onClick={()=>docAction('reference',r.id,'reject')}>Reject</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          }
        </Section>

      </div>
      <style>{`@media(max-width:900px){div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}}`}</style>
    </DashboardLayout>
  );
}

// ── Approved Tutors List ──────────────────────────────────
export function AdminApprovedTutors() {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(()=>{ api.get('/admin/tutors/approved').then(r=>setTutors(r.data.tutors||[])).finally(()=>setLoading(false)); },[]);

  const filtered = tutors.filter(t =>
    t.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.email?.toLowerCase().includes(search.toLowerCase()) ||
    t.subjects?.toLowerCase().includes(search.toLowerCase())
  );

  const suspend = async (id, name) => {
    const reason = window.prompt('Suspension reason for ' + name + ':');
    if (!reason) return;
    await api.post('/admin/tutors/' + id + '/suspend', { reason });
    setTutors(tutors.map(t=>t.id===id?{...t,status:'suspended'}:t));
  };

  const unsuspend = async (id) => {
    if (!window.confirm('Reinstate this tutor?')) return;
    await api.post('/admin/tutors/' + id + '/unsuspend');
    setTutors(tutors.map(t=>t.id===id?{...t,status:'approved'}:t));
  };

  return (
    <DashboardLayout title="Approved Tutors" subtitle="All approved and active tutors">
      <div style={{marginBottom:20,display:'flex',gap:12,alignItems:'center'}}>
        <input type="text" className="form-input" placeholder="Search by name, email or subject..." value={search}
          onChange={e=>setSearch(e.target.value)} style={{maxWidth:360}}/>
        <span style={{fontSize:'0.82rem',color:'var(--ink-400)',fontWeight:600}}>{filtered.length} tutors</span>
      </div>

      {loading ? <div className="loading-screen"><div className="spinner"/></div>
        : filtered.length === 0
        ? <div style={{textAlign:'center',padding:40,color:'var(--ink-400)'}}><p>No tutors found.</p></div>
        : <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {filtered.map(t=>(
            <div key={t.id} className="card" style={{padding:18}}>
              <div style={{display:'flex',gap:14,alignItems:'flex-start',flexWrap:'wrap'}}>
                {t.profile_photo_url
                  ? <img src={t.profile_photo_url} alt={t.full_name} style={{width:52,height:52,borderRadius:'50%',objectFit:'cover',flexShrink:0}}/>
                  : <div className="avatar-placeholder" style={{width:52,height:52,fontSize:'1rem',flexShrink:0}}>{t.full_name?.split(' ').map(w=>w[0]).join('')}</div>
                }
                <div style={{flex:1,minWidth:200}}>
                  <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:4}}>
                    <strong style={{fontSize:'1rem'}}>{t.full_name}</strong>
                    <span className={'badge ' + (t.status==='approved'?'badge-green':'badge-red')}>{t.status}</span>
                  </div>
                  <div style={{fontSize:'0.8rem',color:'var(--ink-400)',marginBottom:6}}>{t.email} · {t.country}</div>
                  {t.subjects && <div style={{fontSize:'0.8rem',color:'var(--ink-500)',marginBottom:4}}><strong>Subjects:</strong> {t.subjects}</div>}
                  {t.levels && <div style={{fontSize:'0.8rem',color:'var(--ink-500)',marginBottom:6}}><strong>Levels:</strong> {t.levels}</div>}
                  <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                    <span style={{fontSize:'0.78rem',color:'var(--ink-400)'}}>{t.total_lessons} lessons · {t.total_bookings} bookings · ★{parseFloat(t.average_rating||0).toFixed(1)}</span>
                    <span style={{fontSize:'0.78rem',color:'var(--green-700)',fontWeight:600}}>
                      Pending payout: {t.currency==='USD'?'$':'GBP'}{parseFloat(t.currency==='USD'?t.payout_balance_usd:t.payout_balance_gbp||0).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <button className="btn btn-secondary btn-sm" onClick={()=>navigate('/admin/tutors/'+t.id)}>View details</button>
                  <a href={'/tutor/'+t.slug} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">Public profile</a>
                  {t.status==='approved'
                    ? <button className="btn btn-danger btn-sm" onClick={()=>suspend(t.id,t.full_name)}>Suspend</button>
                    : <button className="btn btn-primary btn-sm" onClick={()=>unsuspend(t.id)}>Reinstate</button>
                  }
                </div>
              </div>
            </div>
          ))}
        </div>
      }
    </DashboardLayout>
  );
}

// ── Admin Parents ─────────────────────────────────────────
export function AdminParents() {
  return (
    <DashboardLayout title="Parents">
      <div className="alert alert-info">Parent management via Reports section.</div>
    </DashboardLayout>
  );
}

// ── Admin Bookings ────────────────────────────────────────
export function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{ api.get('/admin/reports').then(r=>setBookings(r.data.recentBookings||[])).finally(()=>setLoading(false)); },[]);
  const statusColor = {confirmed:'badge-green',pending:'badge-yellow',cancelled:'badge-red',completed:'badge-grey',disputed:'badge-red'};
  return (
    <DashboardLayout title="Bookings">
      {loading ? <div className="loading-screen"><div className="spinner"/></div>
        : <div className="table-wrap">
          <table>
            <thead><tr><th>ID</th><th>Parent</th><th>Tutor</th><th>Subject</th><th>Status</th><th>Created</th></tr></thead>
            <tbody>
              {bookings.map(b=>(
                <tr key={b.id}>
                  <td style={{fontWeight:700,color:'var(--green-700)'}}>#{b.id}</td>
                  <td>{b.parent_name}</td><td>{b.tutor_name}</td><td>{b.subject}</td>
                  <td><span className={'badge ' + (statusColor[b.status]||'badge-grey')}>{b.status}</span></td>
                  <td style={{fontSize:'0.78rem',color:'var(--ink-400)'}}>{format(new Date(b.created_at),'d MMM yy')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </DashboardLayout>
  );
}

// ── Admin Disputes ────────────────────────────────────────
export function AdminDisputes() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{ api.get('/admin/disputes').then(r=>setDisputes(r.data.disputes||[])).finally(()=>setLoading(false)); },[]);
  const statusColor = {open:'badge-red',under_review:'badge-yellow',resolved:'badge-green',closed:'badge-grey'};
  return (
    <DashboardLayout title="Disputes">
      {loading ? <div className="loading-screen"><div className="spinner"/></div>
        : disputes.length===0
        ? <div style={{textAlign:'center',padding:40,color:'var(--ink-400)'}}><div style={{fontSize:'3rem'}}>✅</div><p>No disputes.</p></div>
        : <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {disputes.map(d=>(
            <div key={d.id} className="card" style={{padding:20}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12}}>
                <div>
                  <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:6}}>
                    <strong>Dispute #{d.id}</strong>
                    <span className={'badge ' + (statusColor[d.status]||'badge-grey')}>{d.status}</span>
                  </div>
                  <p style={{margin:'0 0 6px',fontSize:'0.875rem'}}>{d.reason}</p>
                  <div style={{fontSize:'0.78rem',color:'var(--ink-400)'}}>Raised by: {d.raised_by_name} · {format(new Date(d.created_at),'d MMM yyyy')}</div>
                </div>
                {d.status==='open' && (
                  <button className="btn btn-primary btn-sm" onClick={()=>{
                    const resolution = window.prompt('Resolution:');
                    if(resolution) api.post('/admin/disputes/'+d.id+'/resolve',{resolution}).then(()=>window.location.reload());
                  }}>Resolve</button>
                )}
              </div>
            </div>
          ))}
        </div>
      }
    </DashboardLayout>
  );
}

// ── Admin Payouts ─────────────────────────────────────────
export function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{ api.get('/admin/payouts').then(r=>setPayouts(r.data.payouts||[])).finally(()=>setLoading(false)); },[]);
  const statusColor = {pending:'badge-yellow',processing:'badge-blue',completed:'badge-green',failed:'badge-red',held:'badge-grey'};
  return (
    <DashboardLayout title="Payouts">
      {loading ? <div className="loading-screen"><div className="spinner"/></div>
        : <div className="table-wrap">
          <table>
            <thead><tr><th>Tutor</th><th>Amount</th><th>Period</th><th>Lessons</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {payouts.map(p=>(
                <tr key={p.id}>
                  <td style={{fontWeight:600}}>{p.tutor_name}</td>
                  <td style={{fontWeight:700,color:'var(--green-700)'}}>{p.currency==='USD'?'$':'GBP'}{parseFloat(p.amount).toFixed(2)}</td>
                  <td style={{fontSize:'0.78rem',color:'var(--ink-400)'}}>{format(new Date(p.period_start),'d MMM')} - {format(new Date(p.period_end),'d MMM yy')}</td>
                  <td>{p.lessons_count}</td>
                  <td><span className={'badge ' + (statusColor[p.status]||'badge-grey')}>{p.status}</span></td>
                  <td>
                    {['pending','held','failed'].includes(p.status) && (
                      <button className="btn btn-primary btn-sm" onClick={()=>{
                        const notes = window.prompt('Notes:','Manual payout processed');
                        if(notes!==null) api.post('/admin/payouts/'+p.id+'/manual',{notes}).then(()=>window.location.reload());
                      }}>Mark paid</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    </DashboardLayout>
  );
}

// ── Admin Reports ─────────────────────────────────────────
export function AdminReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{ api.get('/admin/reports').then(r=>setData(r.data)).finally(()=>setLoading(false)); },[]);
  if (loading) return <DashboardLayout title="Reports"><div className="loading-screen"><div className="spinner"/></div></DashboardLayout>;
  return (
    <DashboardLayout title="Reports">
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
        <div className="card">
          <h3 style={{marginBottom:16,fontSize:'1rem'}}>Monthly revenue</h3>
          {!data?.revenue?.length ? <p style={{color:'var(--ink-400)'}}>No data yet.</p>
            : <table style={{width:'100%',fontSize:'0.82rem'}}>
                <thead><tr><th style={{textAlign:'left',padding:'4px 0',color:'var(--ink-400)'}}>Month</th><th style={{textAlign:'left',padding:'4px 0',color:'var(--ink-400)'}}>Lessons</th><th style={{textAlign:'left',padding:'4px 0',color:'var(--ink-400)'}}>Revenue</th></tr></thead>
                <tbody>{data.revenue.map((r,i)=>(
                  <tr key={i}><td style={{padding:'6px 0'}}>{r.month}</td><td>{r.lessons}</td><td style={{fontWeight:700,color:'var(--green-700)'}}>GBP{parseFloat(r.platform_revenue||0).toFixed(2)}</td></tr>
                ))}</tbody>
              </table>
          }
        </div>
        <div className="card">
          <h3 style={{marginBottom:16,fontSize:'1rem'}}>Top tutors</h3>
          {!data?.topTutors?.length ? <p style={{color:'var(--ink-400)'}}>No data yet.</p>
            : data.topTutors.map((t,i)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--ink-50)',fontSize:'0.875rem'}}>
                <span style={{fontWeight:600}}>{t.name}</span>
                <span style={{color:'var(--ink-400)'}}>{t.total_lessons} lessons · ★{parseFloat(t.average_rating||0).toFixed(1)}</span>
              </div>
            ))
          }
        </div>
      </div>
      <style>{`@media(max-width:768px){div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}}`}</style>
    </DashboardLayout>
  );
}

// ── Admin Concerns ────────────────────────────────────────
export function AdminConcerns() {
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{ api.get('/admin/concerns').then(r=>setConcerns(r.data.concerns||[])).finally(()=>setLoading(false)); },[]);
  const statusColor = {open:'badge-red',reviewed:'badge-yellow',closed:'badge-green'};
  return (
    <DashboardLayout title="Concerns">
      {loading ? <div className="loading-screen"><div className="spinner"/></div>
        : concerns.length===0
        ? <div style={{textAlign:'center',padding:40,color:'var(--ink-400)'}}><div style={{fontSize:'3rem'}}>✅</div><p>No concerns reported.</p></div>
        : <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {concerns.map(c=>(
            <div key={c.id} className="card" style={{padding:20}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12}}>
                <div>
                  <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:6}}>
                    <strong>Concern #{c.id}</strong>
                    <span className={'badge ' + (statusColor[c.status]||'badge-grey')}>{c.status}</span>
                  </div>
                  {c.tutor_name && <p style={{margin:'0 0 4px',fontSize:'0.82rem',color:'var(--ink-500)'}}>About tutor: <strong>{c.tutor_name}</strong></p>}
                  <p style={{margin:'0 0 6px',fontSize:'0.875rem'}}>{c.reason}</p>
                  <div style={{fontSize:'0.78rem',color:'var(--ink-400)'}}>Reported by: {c.reporter_name} · {format(new Date(c.created_at),'d MMM yyyy')}</div>
                </div>
                {c.status==='open' && (
                  <button className="btn btn-primary btn-sm" onClick={()=>api.post('/admin/concerns/'+c.id+'/review',{notes:'Reviewed'}).catch(()=>{}).then(()=>window.location.reload())}>
                    Mark reviewed
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      }
    </DashboardLayout>
  );
}

export default AdminDashboard;
